import React from 'react';
import { BellRing } from 'lucide-react';
import EmptyTableState from './EmptyTableState';
import StatusBadge from './StatusBadge';
import TypeBadge from './TypeBadge';

export interface NotificationInboxItem {
  id: string | number;
  title?: string;
  message?: string;
  notification_type?: string;
  type?: string;
  severity?: string;
  status?: string;
  officer_name?: string | null;
  officer_badge?: string | null;
  sender?: string | null;
  related_context?: string | null;
  actionLabel?: string;
  generated_at?: string;
  created_at?: string;
  due_date?: string | null;
}

interface NotificationInboxProps {
  items: NotificationInboxItem[];
  title?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  onSelect?: (item: NotificationInboxItem) => void;
  className?: string;
  toolbar?: React.ReactNode;
}

const joinClasses = (...classes: Array<string | undefined | false>) =>
  classes.filter(Boolean).join(' ');

const humanize = (value?: string | null): string =>
  value ? value.replace(/[_-]+/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) : 'Notification';

const formatDate = (date?: string | null): string => {
  if (!date) return '';
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString();
};

const severityTone = (severity?: string) => {
  const normalized = severity?.toLowerCase();
  if (normalized === 'critical' || normalized === 'high') return 'red';
  if (normalized === 'medium' || normalized === 'warning') return 'amber';
  if (normalized === 'low') return 'green';
  return 'blue';
};

const NotificationInbox: React.FC<NotificationInboxProps> = ({
  items,
  title = 'Notifications',
  emptyTitle = 'No notifications',
  emptyDescription = 'There are no records that need attention right now.',
  onSelect,
  className,
  toolbar,
}) => (
  <section className={joinClasses('notification-inbox', className)} aria-label={title}>
    <div className="notification-inbox-header">
      <div>
        <h2>{title}</h2>
        <p>{items.length} record{items.length === 1 ? '' : 's'}</p>
      </div>
    </div>
    {toolbar ? <div className="notification-inbox-toolbar">{toolbar}</div> : null}
    {items.length === 0 ? (
      <EmptyTableState title={emptyTitle} description={emptyDescription} icon={<BellRing size={22} aria-hidden="true" />} />
    ) : (
      <div className="notification-list">
        {items.map((item) => {
          const notificationType = item.notification_type || item.type || 'notification';
          const actor = item.officer_name || item.officer_badge || item.sender || 'System';
          const date = formatDate(item.due_date || item.generated_at || item.created_at);
          const content = (
            <>
              <div className="notification-item-main">
                <div className="notification-item-title">
                  <strong>{item.title || humanize(notificationType)}</strong>
                  {item.status ? <StatusBadge status={item.status} /> : null}
                </div>
                <p>{item.message || humanize(notificationType)}</p>
                <div className="notification-item-meta">
                  <span>{actor}</span>
                  {item.related_context ? <span>{item.related_context}</span> : null}
                  {date ? <span>{date}</span> : null}
                </div>
              </div>
              <div className="notification-item-side">
                <TypeBadge
                  type={item.severity || notificationType}
                  label={item.severity ? undefined : (item.title || humanize(notificationType))}
                  tone={severityTone(item.severity)}
                />
                {onSelect ? <span className="notification-item-action">{item.actionLabel || 'View Details'}</span> : null}
              </div>
            </>
          );

          return onSelect ? (
            <button
              className="notification-item"
              type="button"
              key={item.id}
              onClick={() => onSelect(item)}
            >
              {content}
            </button>
          ) : (
            <article className="notification-item" key={item.id}>
              {content}
            </article>
          );
        })}
      </div>
    )}
  </section>
);

export default NotificationInbox;
